import { Bookmark, Trash2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { TableViewRow, TableViewScreen } from '@nest-recruit/shared';
import { errorMessage } from '../../lib/api-client';
import { useDeleteTableView, useSaveTableView, useTableViews } from '../../lib/table-views-api';
import { useToasts } from '../../stores/toasts';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { MenuGroup, MenuItem } from '../Popover';
import styles from './TableViews.module.css';

/** What a screen hands the table so its filters can be named and brought back (Q28). */
export interface TableViewsConfig {
  readonly screen: TableViewScreen;
  /** The address bar's filter strings as they stand. */
  readonly filters: Readonly<Record<string, string>>;
  readonly onApply: (filters: Readonly<Record<string, string>>) => void;
}

interface TableViewsProps {
  readonly config: TableViewsConfig;
  readonly hiddenColumns: readonly string[];
  readonly onApplyColumns: (hiddenColumns: readonly string[]) => void;
  readonly onDone: () => void;
}

/**
 * 06-qol §3: "save the current filter and column combination as a named view, private or shared
 * with the team". Lives in the table's columns menu, so every record table has it the same way.
 */
export function TableViews({ config, hiddenColumns, onApplyColumns, onDone }: TableViewsProps): ReactNode {
  const views = useTableViews(config.screen, true);
  const save = useSaveTableView(config.screen);
  const remove = useDeleteTableView(config.screen);
  const show = useToasts((state) => state.show);
  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function apply(view: TableViewRow): void {
    config.onApply(view.state.filters);
    onApplyColumns(view.state.hiddenColumns);
    onDone();
  }

  async function saveCurrent(): Promise<void> {
    try {
      await save.mutateAsync({ screen: config.screen, name: name.trim(), isShared, state: { filters: { ...config.filters }, hiddenColumns: [...hiddenColumns] } });
      show({ tone: 'success', message: `View "${name.trim()}" saved`, detail: isShared ? 'Your team can use it too.' : 'Only you can see it.' });
      setName('');
      setIsShared(false);
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  const rows = views.data ?? [];
  return (
    <>
      <MenuGroup label="Saved views">
        {rows.length === 0 && <span className={styles.none}>{views.isPending ? 'Loading…' : 'None yet — save the one you are looking at.'}</span>}
        {rows.map((view) => (
          <div key={view.id} className={styles.view}>
            <MenuItem icon={view.isShared ? <Users size={14} /> : <Bookmark size={14} />} meta={view.isMine ? undefined : view.ownerName ?? undefined} onClick={() => apply(view)}>
              {view.name}
            </MenuItem>
            {view.isMine && (
              <Button
                size="sm"
                variant="ghost"
                icon={Trash2}
                aria-label={`Delete the view ${view.name}`}
                onClick={() =>
                  remove.mutate(view, {
                    onSuccess: () => show({ tone: 'success', message: `View "${view.name}" deleted` }),
                    onError: (cause) => show({ tone: 'danger', message: "The view wasn't deleted", detail: errorMessage(cause) }),
                  })
                }
              />
            )}
          </div>
        ))}
      </MenuGroup>
      <form
        className={styles.save}
        onSubmit={(event) => {
          event.preventDefault();
          void saveCurrent();
        }}
      >
        <Input aria-label="Name for this view" placeholder="Name this view" value={name} maxLength={60} isInvalid={error !== null} onChange={(event) => setName(event.target.value)} />
        <Checkbox checked={isShared} onChange={setIsShared} label="Share with the team" />
        {error && <p className={styles.error}>{error}</p>}
        <Button type="submit" size="sm" variant="outline" isPending={save.isPending} disabled={name.trim() === ''}>
          Save view
        </Button>
      </form>
    </>
  );
}
